import { Messages } from '../i18n/messages';

export enum Role {
  ADMIN = "admin",
  NEWS = "news",
  NEWS_WRITE = "news.write",
  NEWS_READ = "news.read",
  PAGES = "pages",
  PAGES_WRITE = "pages.write",
  PAGES_READ = "pages.read",
  NOTIFICATION = "notification",
  NOTIFICATION_PUSH = "notification.push",
  CARPOOLS = "carpools",
  CARPOOLS_VIEWER = "carpools.viewer",
  ADVERTISEMENTS = "advertisements",
  ADVERTISEMENTS_WRITE = "advertisements.write",
  ADVERTISEMENTS_READ = "advertisements.read",
  MODERATOR = "moderator",
  MODERATOR_CARPOOLS = "moderator.carpools",
}

/** The roles an admin gives, in the Users tab and when creating a user */
export const SETTABLE_ROLES = [
  Role.ADMIN,
  Role.NEWS_WRITE,
  Role.PAGES_WRITE,
  Role.ADVERTISEMENTS_WRITE,
  Role.NOTIFICATION_PUSH,
  Role.CARPOOLS,
  Role.CARPOOLS_VIEWER,
  Role.MODERATOR,
] as const;

export type SettableRole = typeof SETTABLE_ROLES[number];

export interface RoleOption {
  value: SettableRole;
  label: string;
  description: string;
}

/** The settable roles with their names, in the language shown: `settableRoleOptions(useMessages('roles'))` */
export const settableRoleOptions = (t: Messages<'roles'>): RoleOption[] =>
  SETTABLE_ROLES.map(value => ({ value, label: t.labels[value], description: t.descriptions[value] }));

/**
 * Parent roles that grant exactly what a settable role grants, since the API never asks for them, only for their
 * children ("news" gives news.read and news.write, as news.write does). Users created before the roles list existed
 * have them. Not "moderator.carpools": routes ask for "moderator" itself.
 */
const SETTABLE_EQUIVALENT: Partial<Record<string, SettableRole>> = {
  [Role.NEWS]: Role.NEWS_WRITE,
  [Role.PAGES]: Role.PAGES_WRITE,
  [Role.ADVERTISEMENTS]: Role.ADVERTISEMENTS_WRITE,
  [Role.NOTIFICATION]: Role.NOTIFICATION_PUSH,
};

/** A user's roles as the Users tab shows and edits them: each parent role above as its settable equivalent */
export const asSettableRoles = (roles: string[]): string[] =>
  Array.from(new Set(roles.map(role => SETTABLE_EQUIVALENT[role] ?? role)));

/** A role's name in the language shown, or the role itself when it has none */
export const roleLabel = (role: string, t: Messages<'roles'>): string =>
  (t.labels as Record<string, string>)[role] ?? (t.otherLabels as Record<string, string>)[role] ?? role;

export type EditorType = 'infos' | 'news' | 'pois' | 'artists' | 'shows' | 'map' | 'tickets' | 'history' | 'notifications' | 'users' | 'carpools' | 'reporting' | 'pages' | 'advertisements';

export const getAccessibleEditors = (roles: string[]): EditorType[] => {
  const allowedEditors = new Set<EditorType>();

  if (roles.includes(Role.ADMIN)) {
    return ['infos', 'news', 'pois', 'artists', 'shows', 'map', 'tickets', 'history', 'notifications', 'users', 'carpools', 'reporting', 'pages', 'advertisements'];
  }

  // Add pages access
  if (roles.some(role => role.startsWith('pages'))) {
    allowedEditors.add('pages');
  }

  // Add moderator access
  if (roles.includes(Role.MODERATOR)) {
    allowedEditors.add('reporting');
  }

  // News related roles
  if (roles.some(role => role.startsWith('news'))) {
    allowedEditors.add('news');
  }

  // Notification related roles
  if (roles.some(role => role.startsWith('notification'))) {
    allowedEditors.add('notifications');
  }

  // Carpools related roles
  if (roles.some(role => role.startsWith('carpools'))) {
    allowedEditors.add('carpools');
  }

  // Advertisements related roles
  if (roles.some(role => role.startsWith('advertisements'))) {
    allowedEditors.add('advertisements');
  }

  return Array.from(allowedEditors);
}; 